"use client"

import { createContext, useContext, useMemo, type ReactNode } from "react"
import { useRouter } from "next/navigation"
import { createTranslator, localizeData, localizeTree, type Dictionary, type Translator } from "./translate"
import { localeHref, type Locale } from "./routing"
import { useSiteImages } from "@/cms/site-images-client"

const Context = createContext<{ locale: Locale; t: Translator }>({ locale: "en", t: text => text })
export function LocaleProvider({ locale, dictionary, children }: { locale: Locale; dictionary: Dictionary; children: ReactNode }) {
  const value = useMemo(() => ({ locale, t: createTranslator(dictionary, locale) }), [locale, dictionary])
  return <Context value={value}>{children}</Context>
}
export function useI18n() {
  const { locale, t } = useContext(Context)
  return { locale, t, href: (path: string) => localeHref(path, locale), data: <T,>(value: T) => localizeData(value, t, locale) }
}
export function Localized({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n()
  const images = useSiteImages()
  return localizeTree(children, t, locale, images)
}
export function useLocalizedRouter() {
  const router = useRouter()
  const { locale } = useI18n()
  return useMemo(() => ({ ...router, push: (href: string, options?: Parameters<typeof router.push>[1]) => router.push(localeHref(href, locale), options), replace: (href: string, options?: Parameters<typeof router.replace>[1]) => router.replace(localeHref(href, locale), options) }), [router, locale])
}
